import {
  Dialog,
  Button,
  useTheme,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  messages,
  constants,
  IpdSearchOptions,
  IPD_SEARCH_TERMS,
  DeviceMappingData,
} from './Constants'
import { InfoRow } from './InfoRow'
import { styles } from './ProRithmDasboard'
import { stringToTitleCase } from '../membership/utils'
import { FC, useCallback, useEffect, useState } from 'react'
import { searchPatient } from '../../services/ProrithmServices'
import useCustomStyles from '../../utils/hooks/CustomStylesHook'

interface DeviceMappingDetailsProps {
  open: boolean
  setOpen: (open: boolean) => void
  deviceMappingDetails: DeviceMappingData | null
}

const DeviceMappingDetails: FC<DeviceMappingDetailsProps> = ({
  open,
  setOpen,
  deviceMappingDetails,
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [patientDetails, setPatientDetails] = useState<IpdSearchOptions | null>(
    null
  )

  const getPatientData = useCallback(async () => {
    const isIpdCategory =
      deviceMappingDetails?.categoryType === constants?.ipd ? true : false
    if (!deviceMappingDetails?.patientId) {
      console.warn(messages?.patientIdNotFound)
      return
    }

    const response = await searchPatient(
      IPD_SEARCH_TERMS?.[0]?.value,
      deviceMappingDetails?.patientId,
      isIpdCategory
    )
    if (response?.success) {
      setPatientDetails(response?.data?.[0] ?? null)
    } else {
      setPatientDetails(null)
    }
  }, [deviceMappingDetails?.categoryType, deviceMappingDetails?.patientId])

  useEffect(() => {
    if (deviceMappingDetails) {
      getPatientData()
    }

    return () => {
      setPatientDetails(null)
    }
  }, [deviceMappingDetails, getPatientData])

  const patientData = [
    { label: 'Patient Id', value: deviceMappingDetails?.patientId },
    {
      label: 'First Name',
      value: stringToTitleCase(patientDetails?.firstName ?? ''),
    },
    {
      label: 'Last Name',
      value: stringToTitleCase(patientDetails?.lastName ?? ''),
    },
    {
      label: 'Age',
      value: patientDetails?.age?.toString(),
    },
    {
      label: 'Gender',
      value: stringToTitleCase(patientDetails?.gender ?? ''),
    },
    {
      label: 'Phone Number',
      value: patientDetails?.phoneNumber,
    },
  ]

  const ambulanceData = [
    {
      label: 'Location',
      value: deviceMappingDetails?.ambulance?.ambulanceLocation,
    },
    {
      label: 'Ambulance Number',
      value: deviceMappingDetails?.ambulance?.ambulanceVehicleNumber,
    },
    {
      label: 'Driver Phone',
      value: deviceMappingDetails?.ambulance?.driverPhone,
    },
    {
      label: 'EMT Number',
      value: deviceMappingDetails?.ambulance?.emtNumber,
    },
  ]

  const locationData = [
    {
      label: 'Location',
      value: deviceMappingDetails?.location?.locationName,
    },
    {
      label: 'Location Phone Number',
      value: deviceMappingDetails?.location?.locationPhoneNumber,
    },
    {
      label: 'Location Type',
      value: deviceMappingDetails?.location?.locationType,
    },
  ]

  const homekareData = [
    {
      label: 'Executive Name',
      value: deviceMappingDetails?.homekare?.executiveName,
    },
    {
      label: 'Executive Phone Number',
      value: deviceMappingDetails?.homekare?.executivePhoneNumber,
    },
  ]

  const ipdData = [
    {
      label: 'Bed Type',
      value: deviceMappingDetails?.ipd?.bedType,
    },
    {
      label: 'Bed Name',
      value: deviceMappingDetails?.ipd?.bedName,
    },
    {
      label: 'Room Name',
      value: deviceMappingDetails?.ipd?.roomName,
    },
    {
      label: 'Ward Name',
      value: deviceMappingDetails?.ipd?.wardName,
    },
  ]

  return (
    <Dialog fullWidth maxWidth='md' open={open} onClose={() => setOpen(false)}>
      <DialogTitle align='center'>{constants?.mappingDetails}</DialogTitle>
      <DialogContent>
        <InfoRow label='Device Type' value={deviceMappingDetails?.deviceType} />
        <InfoRow label='Device Id' value={deviceMappingDetails?.deviceId} />
        {patientData?.map(
          ({ label, value }) =>
            value && <InfoRow key={label} label={label} value={value} />
        )}
        {deviceMappingDetails?.categoryType && (
          <InfoRow
            label='Category Type'
            value={deviceMappingDetails?.categoryType}
          />
        )}
        {deviceMappingDetails?.categoryType === constants?.ambulanceText && (
          <>
            {ambulanceData?.map((row) => (
              <InfoRow
                key={crypto.randomUUID()}
                label={row?.label}
                value={row?.value}
              />
            ))}
          </>
        )}
        {deviceMappingDetails?.categoryType === constants?.location && (
          <>
            {locationData?.map((row) => (
              <InfoRow
                key={crypto.randomUUID()}
                label={row?.label}
                value={row?.value}
              />
            ))}
          </>
        )}
        {deviceMappingDetails?.categoryType === constants?.homekare && (
          <>
            {homekareData?.map((row) => (
              <InfoRow
                key={crypto.randomUUID()}
                label={row?.label}
                value={row?.value}
              />
            ))}
          </>
        )}
        {deviceMappingDetails?.categoryType === constants?.ipd && (
          <>
            {ipdData?.map((row) => (
              <InfoRow
                key={crypto.randomUUID()}
                label={row?.label}
                value={row?.value}
              />
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          size='small'
          variant='contained'
          id='amb-details-btn'
          onClick={() => setOpen(false)}
          className={classes?.proRithmDasboardCreateBtn}
        >
          {constants?.close}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DeviceMappingDetails
